<template>
  <div>
    <!-- MODO “TELA NORMAL” -->
    <div v-if="!printMode">
      <h6>Matrículas Novas (Anual)</h6>
      <p></p>
      <div class="row mb-3">
        <div class="col-auto">
          <label for="ano_referencia">Ano referência:</label>
          <input
            id="ano_referencia"
            type="number"
            class="form-control"
            v-model.number="anoReferenciaLocal"
            min="2000"
            max="2100"
          />
        </div>
        <div class="col-auto align-self-end">
          <button @click="abrirPrint" class="btn btn-primary">
            Executar
          </button>
        </div>
      </div>
    </div>

    <!-- MODO “IMPRESSÃO / PRINT” -->
    <div v-else class="relatorio-body">
      <div v-if="loading">Carregando…</div>

      <div v-else-if="dados">
        <p class="text-center" style="font-size: 13px;">
          Ano de referência: <strong>{{ dados.anoReferencia }}</strong>
          &nbsp; | &nbsp;
          Ano anterior: <strong>{{ dados.anoAnterior }}</strong>
        </p>

        <!-- 🔹 GRÁFICO LINHA: MATRÍCULAS POR MÊS -->
        <div class="chart-container">
          <Line
            v-if="chartData"
            :data="chartData"
            :options="chartOptions"
          />
        </div>

        <!-- Tabela Resumo Anual -->
        <div class="mt-3">
          <h6>Matrículas Novas</h6>
          <table class="table table-bordered table-sm" style="font-size: 12px;">
            <thead>
              <tr>
                <th style="width: 20%;">Ano</th>
                <th style="width: 40%;">Total</th>
                <th style="width: 40%;">Média mensal</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ dados.anoReferencia }}</td>
                <td>{{ dados.referencia.total }}</td>
                <td>{{ formatarNumero(dados.referencia.mediaMensal) }}</td>
              </tr>
              <tr>
                <td>{{ dados.anoAnterior }}</td>
                <td>{{ dados.anterior.total }}</td>
                <td>{{ formatarNumero(dados.anterior.mediaMensal) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Tabela Mensal -->
        <div class="mt-3">
          <h6>Matrículas por mês</h6>
          <table class="table table-bordered table-sm" style="font-size: 11px;">
            <thead>
              <tr>
                <th>Ano</th>
                <th v-for="(mes, idx) in mesesNomes" :key="'mes-' + idx">
                  {{ mes }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="dados.anterior">
                <td>{{ dados.anoAnterior }}</td>
                <td v-for="(qtd, idx) in dados.anterior.meses" :key="'ant-' + idx">
                  {{ qtd }}
                </td>
              </tr>
              <tr v-if="dados.referencia">
                <td>{{ dados.anoReferencia }}</td>
                <td v-for="(qtd, idx) in dados.referencia.meses" :key="'ref-' + idx">
                  {{ qtd }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p style="font-size: 11px; font-style: italic; margin-top: 8px;">
          Não contabilizadas as renovações – apenas matrículas efetivadas no ano de referência.
        </p>
      </div>

      <div v-else>
        <p class="text-muted">Nenhum dado encontrado para o ano informado.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '@/services/axios'

// vue-chartjs + chart.js
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

// 🔹 registra componentes do Chart.js (se já tiver um registro global em outro lugar, pode remover esta parte)
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

const props = defineProps({
  printMode:  { type: Boolean, default: false },
  printTitle: String
})

const router = useRouter()
const route  = useRoute()

const loading            = ref(false)
const dados              = ref(null)
const anoReferenciaLocal = ref(new Date().getFullYear())

const mesesNomes = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
]

async function carregarDados () {
  loading.value = true
  try {
    const anoRef = parseInt(route.query.anoReferencia, 10) || new Date().getFullYear()
    const res = await axios.get('/gestao/relatorios/operacionais/matriculas-novas-anual', {
      params: { ano: anoRef }
    })
    dados.value = res.data || null
  } catch (e) {
    console.error('Erro em MatriculasNovasAnual (print):', e)
    dados.value = null
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  if (props.printMode) {
    carregarDados()
  }
})

function abrirPrint () {
  const anoRef = anoReferenciaLocal.value || new Date().getFullYear()
  const subtitle = `Ano de referência: ${anoRef} (comparado a ${anoRef - 1})`

  router.push({
    path: '/gestao/relatorios/print/matriculas-novas-anual',
    query: {
      title:    'Matrículas Novas (Anual)',
      subtitle: subtitle,
      anoReferencia: anoRef
    }
  })
}

function formatarNumero (valor) {
  if (valor == null || isNaN(valor)) return '0,00'
  return valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

/**
 * Dados do gráfico de linha (vue-chartjs)
 */
const chartData = computed(() => {
  if (!dados.value) return null

  return {
    labels: mesesNomes,
    datasets: [
      {
        label: `Ano ${dados.value.anoAnterior}`,
        data: dados.value.anterior?.meses || [],
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        tension: 0.2,
        fill: false
      },
      {
        label: `Ano ${dados.value.anoReferencia}`,
        data: dados.value.referencia?.meses || [],
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.2,
        fill: false
      }
    ]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom'
    },
    title: {
      display: true,
      text: 'Matrículas novas por mês'
    },
    tooltip: {
      mode: 'index',
      intersect: false
    }
  },
  interaction: {
    mode: 'index',
    intersect: false
  },
  scales: {
    x: {
      title: {
        display: true,
        text: 'Meses'
      }
    },
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: 'Qtd. matrículas'
      },
      ticks: {
        precision: 0
      }
    }
  }
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 1rem;
}
.mt-3 {
  margin-top: 1rem;
}
.table-sm th,
.table-sm td {
  padding: 0.2rem 0.3rem;
}

/* 🔹 container do gráfico para manter altura adequada */
.chart-container {
  width: 100%;
  height: 280px;
  margin-bottom: 16px;
}
</style>
